/**
 * TODOS los umbrales del motor viven aqui. Un numero magico suelto en otro
 * archivo del motor es un bug: hace imposible ajustar las reglas sin cazar
 * constantes por el codigo. SPEC 7.
 */

import type { ActivityLevel, GoalDirection, GoalPace } from './types';

// --- Gasto energetico ---

/** SPEC 6.A: factor de actividad entre 1,2 y 1,9. */
export const ACTIVITY_FACTORS: Record<ActivityLevel, number> = {
  sedentario: 1.2,
  ligero: 1.375,
  moderado: 1.55,
  alto: 1.725,
  muy_alto: 1.9,
};

/**
 * Ajuste sobre el TDEE segun objetivo y ritmo.
 * SPEC 6.A: perdida -10 % a -20 %; ganancia +5 % a +10 %.
 */
export const GOAL_ADJUSTMENT: Record<GoalDirection, Record<GoalPace, number>> = {
  perder: { suave: -0.1, normal: -0.15, agresivo: -0.2 },
  mantener: { suave: 0, normal: 0, agresivo: 0 },
  ganar: { suave: 0.05, normal: 0.075, agresivo: 0.1 },
};

// --- Perfil ---

/**
 * Límites de un perfil con el que se puede calcular. La edad mínima no es una
 * preferencia: las referencias de la EFSA que usa el motor son de adultos.
 */
export const PROFILE_LIMITS = {
  ageYears: { min: 18, max: 110 },
  heightCm: { min: 100, max: 250 },
  weightKg: { min: 30, max: 300 },
} as const;

// --- Macros ---

/** SPEC 6.A: proteina 1,6-2,2 g/kg, por defecto 1,8. */
export const PROTEIN_PER_KG = { min: 1.6, default: 1.8, max: 2.2 } as const;

/** SPEC 6.A: la grasa no baja del 25 % de las kcal. */
export const MIN_FAT_RATIO = 0.25;

export const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9, alcohol: 7 } as const;

/** SPEC 6.A: limite superior de grasa saturada, en % de las kcal. */
export const MAX_SATURATED_FAT_RATIO = 0.1;

// --- Cobertura ---

/**
 * SPEC 7.1 y 7.5: por debajo de esto el nutriente se marca "datos
 * insuficientes" y NO se trata como deficit.
 */
export const MIN_COVERAGE = 0.7;

// --- Ponderacion historica (SPEC 7.5) ---

export const HISTORY = {
  /** Tope del ratio diario ingesta/objetivo. */
  ratioCap: 1.5,
  /** Semivida de la media ponderada exponencial, en dias. */
  halfLifeDays: 14,
  /** Minimo de dias validos en los ultimos 7 para poder opinar. */
  minValidDaysIn7: 4,
  /** Media de los ultimos 30 dias validos que silencia cualquier alerta. */
  silenceThreshold: 0.9,
  /** Por debajo de esto (EMA y ultimos 7) salta la alerta. */
  alertThreshold: 0.8,
  /** Ventana de la regla de silencio. */
  silenceWindowDays: 30,
  recentWindowDays: 7,
  /**
   * Hasta donde se rebusca hacia atras para reunir esos 30 dias VALIDOS.
   *
   * Las ventanas del spec se cuentan en dias validos, no en dias de calendario:
   * quien marca completo uno de cada tres dias necesita 90 dias de calendario
   * para tener 30 validos. Con un limite fijo de 45 dias la regla de silencio
   * no llegaba a cumplirse nunca y la media exponencial salia recortada.
   */
  maxLookbackDays: 365,
} as const;

// --- Peso y TDEE adaptativo (SPEC 6.A) ---

export const WEIGHT_TREND = {
  /** Semivida de la media movil exponencial del peso, en dias. */
  halfLifeDays: 10,
  /** Sin dos pesadas no hay pendiente que medir. */
  minPointsForRate: 2,
} as const;

/**
 * Coeficientes de Mifflin-St Jeor (SPEC 6.A). No son ajustables: son LA
 * formula publicada. Estan aqui para que se puedan leer y comprobar contra el
 * paper sin bucear en el codigo.
 */
export const MIFFLIN = {
  perKg: 10,
  perCm: 6.25,
  perYear: 5,
  hombre: 5,
  mujer: -161,
} as const;

/** Fracciones de envase que ofrece la interfaz (SPEC 6.B). */
export const PACKAGE_FRACTIONS = {
  entero: 1,
  medio: 0.5,
  cuarto: 0.25,
} as const;

export const ADAPTIVE_TDEE = {
  /** Dias minimos con peso y registro completo antes de estimar nada. */
  minDays: 14,
  /** kcal por kg de peso corporal ganado o perdido. */
  kcalPerKg: 7700,
  /**
   * A los 14 dias se mezcla poco; a los 42 se confia del todo en lo medido.
   * Entre medias, interpolacion lineal.
   */
  fullConfidenceDays: 42,
} as const;

// --- Entrenamiento (SPEC 7.2 a 7.4) ---

export const TRAINING = {
  /** Subir peso si todas las series efectivas llegan al tope con este RIR o mas. */
  progressRirThreshold: 2,
  /** Sesiones seguidas fallando el minimo con RIR 0 antes de proponer bajar. */
  deloadFailedSessions: 2,
  /** Sesiones sin mejorar el 1RM estimado antes de proponer cambio. */
  stalledSessions: 3,
  /** Recorte propuesto al descargar. */
  deloadRange: { min: 0.05, max: 0.1 },
  /** Una serie cuenta como efectiva si no es de calentamiento y RIR <= esto. */
  effectiveSetMaxRir: 3,
  /** Peso de la serie para el musculo principal y para el secundario. */
  primaryWeight: 1,
  secondaryWeight: 0.5,
  /** Minimo de series efectivas por musculo y semana. */
  minWeeklySetsPerMuscle: 6,
  /** Proporcion empuje/traccion fuera de este rango = descompensacion. */
  pushPullRatio: { min: 0.67, max: 1.5 },
  /** Caida de rendimiento que delata fatiga local, frente a su propio historico. */
  fatigueDropRatio: 0.15,
} as const;

// --- Cierre de dieta (SPEC 7.1) ---

export const CLOSE_DAY = {
  /** Maximo de alimentos en una propuesta. */
  maxFoods: 3,
  /** Las porciones se mueven en pasos de 25 g para que sean realistas. */
  gramStep: 25,
  minGrams: 25,
  maxGrams: 300,
  /** No superar las kcal restantes mas este margen. */
  kcalTolerance: 0.05,
  /**
   * Con un tope (sodio, saturadas) YA superado, lo más que puede añadir toda la
   * propuesta, en fracción del tope: 1 % = 20 mg de sodio. Casi todo lleva
   * trazas (una fruta, 1-3 mg); con "nada" no se podría proponer nada.
   */
  overLimitAllowance: 0.01,
  /** Cuantos candidatos se consideran como mucho. */
  maxCandidates: 150,
  /** Empujon a los alimentos que el usuario ya suele comer. */
  familiarityBonus: 0.15,
  /**
   * Margen de coma flotante para dar una cobertura por completa. No es un
   * umbral de producto: sumar fracciones rara vez da 1 exacto.
   */
  fullCoverageEpsilon: 0.001,
} as const;

// --- Avisos ---

/** SPEC 7: nunca mas de 3 avisos visibles a la vez. */
export const MAX_VISIBLE_ADVICE = 3;

/** Nutrientes que se nombran al justificar una propuesta de cierre. */
export const MAX_REASON_NUTRIENTS = 3;
